import React, { useCallback, useEffect, useState } from 'react';
import { BaseButton } from '@components';
import { NOT_FOUND_STATUS_CODE } from '@constants/api.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import { getClient, getPreferredStartPage } from '@store/services/localStorage.service.ts';
import type { GeneralError } from '@types';
import { getCallbackUrl } from '@utils/navigation.ts';

import { Content, Header, StatusPageWrapper, Subheader } from './styled.tsx';

interface Props {
    image: React.ReactNode;
    header: string;
    subheader: string;
    content: React.ReactNode;
    showBackToHomeBtn?: boolean;
    fetchClientData?: boolean;
    useCallbackUrlForBackButton?: boolean;
}

const isLandingPageDefined = (data: any) =>
    data && typeof data === 'object' && 'preferences' in data && data.preferences.landingPage;

const redirectToNotFoundIfNoTenant = () => {
    const { pathname } = window.location;

    if (pathname === '/' || pathname === '') {
        window.location.replace(ROUTES.DEFAULT.NOT_FOUND);
    }
};

const isTenantInvalid = (err: any, isMaintenance: boolean | undefined) =>
    err &&
    [NOT_FOUND_STATUS_CODE].includes(err?.status) &&
    !isMaintenance &&
    !window.location.pathname.includes(ROUTES.DEFAULT.NOT_FOUND);

const StatusPage: React.FC<Props> = ({
    image,
    header,
    subheader,
    content,
    showBackToHomeBtn = true,
    fetchClientData = true,
    useCallbackUrlForBackButton = false,
}) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const [homeUrl, setHomeUrl] = useState(ROUTES.HOME);
    const client = getClient();

    const callbackUrl = getCallbackUrl(new URLSearchParams(window.location.search));
    const backToHomeUrl = useCallbackUrlForBackButton && callbackUrl ? callbackUrl : homeUrl;

    const healthStatus = useAppSelector((state) => state.health.healthStatus);
    const { isMaintenance } = healthStatus?.maintenanceInfo || {};

    const setUrlBasedOnPreferredStartPage = useCallback(() => {
        const preferredStartPage = getPreferredStartPage();

        if (preferredStartPage) {
            setHomeUrl(`${window.location.origin}/${preferredStartPage}`);
        }
    }, []);

    const handleBackToHome = () => window.location.replace(backToHomeUrl);

    useEffect(() => {
        if (!fetchClientData) return;

        redirectToNotFoundIfNoTenant();

        dispatch(getFullUserInfo())
            .then((data) => {
                if (isLandingPageDefined(data)) {
                    setHomeUrl(`${window.location.origin}/${data.preferences.landingPage}`);
                } else {
                    setUrlBasedOnPreferredStartPage();
                }
            })
            .catch((err: GeneralError) => {
                if (isTenantInvalid(err, isMaintenance)) {
                    window.location.replace(`${window.location.origin}/${ROUTES.DEFAULT.NOT_FOUND}`);
                }

                setUrlBasedOnPreferredStartPage();
            });
    }, [client, dispatch, fetchClientData, isMaintenance, setUrlBasedOnPreferredStartPage]);

    return (
        <StatusPageWrapper>
            {image}
            <Header>{header}</Header>
            <Subheader>{subheader}</Subheader>
            <Content>{content}</Content>
            {!!showBackToHomeBtn && <BaseButton onClick={handleBackToHome}>{t`Back to home`}</BaseButton>}
        </StatusPageWrapper>
    );
};

export default StatusPage;
